import { css, MAGNETIC_TOKENS } from '@appd/uik';
import { SectionCardPropsEntry } from './SectionCard';

const p2c = (cx: number, cy: number, phi: number, r: number) => `${cx + r * Math.cos(phi)} ${cy + r * Math.sin(phi)}`;

const arc = (phi: number, dphi: number, c: number, r: number) =>
  `M ${p2c(c, c, phi, r)} A ${r}, ${r}, 0, ${dphi > Math.PI ? 1 : 0}, 1, ${p2c(c, c, phi + dphi, r)}`;

const DEFAULT_COLOR = '#D0D0D0';

const { NEGATIVE_BG, WARNING_BG, POSITIVE_BG, DEFAULT_BG_STRONG, CONTROL_BG_STRONG } = MAGNETIC_TOKENS.COLORS;

function healthColor(status: string): string {
  switch (status) {
    case '3':
      return NEGATIVE_BG;
    case '2':
      return WARNING_BG;
    case '0':
      return POSITIVE_BG;
    case 'null':
      return CONTROL_BG_STRONG;
    default:
      return DEFAULT_BG_STRONG;
  }
}

const healthText: Record<string, string> = {
  '0': 'Normal',
  '1': 'Unknown',
  '2': 'Warning',
  '3': 'Critical',
  null: 'Not configured',
};

type EntityHealthDonutProps = {
  size: number;
  entry: SectionCardPropsEntry;
};

function countSize(count: number): number {
  return 32 - Math.max(0, 4 * (String(count).length - 3));
}

export default function EntitytHealthDonut({ size, entry }: EntityHealthDonutProps): JSX.Element {
  const { healthGroups } = entry;

  const groupIndex: Record<string, number> = { '0': 3, '1': 4, '2': 2, '3': 1, null: 5 };
  const sortedGroups = [];
  for (const g of healthGroups) {
    sortedGroups[groupIndex[g.health ?? 'null']] = g;
  }

  const totalCount = healthGroups.reduce((a: number, { count }: { count: number }) => a + count, 0);

  const thickness = 24;
  const c = size / 2;
  const r = c - 0.5 * thickness;

  let phi = -Math.PI / 2;

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        padding: MAGNETIC_TOKENS.SPACING[6],
        border: `2px solid ${MAGNETIC_TOKENS.COLORS.CONTROL_BORDER_WEAK}`,
        gap: MAGNETIC_TOKENS.SPACING[4],
        borderRadius: MAGNETIC_TOKENS.RADIUS.HEAVY,
      })}
    >
      <div
        className={css({
          position: 'relative',
          width: size,
          height: size,
          display: 'flex',
          gap: MAGNETIC_TOKENS.SPACING[2],
        })}
      >
        <svg width={size} height={size} overflow="visible">
          {sortedGroups.map((s, index) => {
            const dPhi = (s.count / totalCount) * Math.PI * 2;
            const lastPhi = phi;
            phi = lastPhi + dPhi;
            return (
              <path
                // eslint-disable-next-line react/no-array-index-key
                key={`s${index}`}
                d={arc(lastPhi, dPhi - 0.02, c, r)}
                stroke={healthColor(s.health) || DEFAULT_COLOR}
                strokeWidth={thickness}
                fill="none"
                strokeLinecap="butt"
              />
            );
          })}
        </svg>
        <div
          className={css({
            position: 'absolute',
            left: 0,
            top: 0,
            width: size,
            height: size,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: MAGNETIC_TOKENS.SPACING[2],
            })}
          >
            <div
              className={css({
                fontSize: countSize(totalCount),
                fontWeight: 700,
                marginTop: MAGNETIC_TOKENS.SPACING[3],
              })}
            >
              {totalCount ?? ''}
            </div>
            <div className={css({ fontSize: 18, fontWeight: 500 })}>Total</div>
          </div>
        </div>
      </div>
      <div className={css({ display: 'flex', flexDirection: 'column', gap: MAGNETIC_TOKENS.SPACING[1] })}>
        {['3', '2', '0', '1', 'null'].map(status => (
          <div key={healthText[status]} className={css({ display: 'flex', alignItems: 'center' })}>
            <div
              className={css({
                height: MAGNETIC_TOKENS.SPACING[4],
                width: MAGNETIC_TOKENS.SPACING[4],
                borderRadius: 2,
                margin: 6,
                backgroundColor: healthColor(status),
              })}
            />
            <div className={css({ fontSize: 12 })}>{healthText[status]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
